import { AxiosConfigExplore } from "Explore/Config/AxiosConfig";

const axios = AxiosConfigExplore();
const STORAGE_KEY = "academic_saved_search_results";

const getLocalCollections = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error("Failed to read saved search results from localStorage", e);
    return [];
  }
};

const saveLocalCollections = (collections) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collections));
  } catch (e) {
    console.error("Failed to save search results to localStorage", e);
  }
};

const normalizeCollection = (col) => {
  if (!col) return col;
  return {
    ...col,
    entityType: col.entity_type || col.entityType,
    entity_type: col.entity_type || col.entityType,
    createdAt: col.created_at || col.createdAt,
    created_at: col.created_at || col.createdAt,
    updatedAt: col.updated_at || col.updatedAt,
    updated_at: col.updated_at || col.updatedAt,
  };
};

export const getSavedSearchResults = async ({ entityType } = {}) => {
  try {
    const params = { rows_per_page: 100 };
    if (entityType) {
      params.entity_type = entityType;
    }
    const res = await axios.get("/user/saved-search-results", { params });
    if (res && res.data) {
      const serverItems = Array.isArray(res.data)
        ? res.data
        : (res.data.items || res.data.results || []);
      return serverItems.map(normalizeCollection);
    }
  } catch (err) {
    console.warn("Backend saved-search-results fetch failed, checking local cache:", err);
  }

  const local = getLocalCollections().map(normalizeCollection);
  if (entityType) {
    const normalized = entityType.toLowerCase();
    return local.filter((c) => {
      const type = (c.entityType || c.entity_type || "").toLowerCase();
      return type === normalized || type === `${normalized}s` || `${type}s` === normalized;
    });
  }
  return local;
};

export const getSavedSearchResultById = async (id) => {
  try {
    const url = `/user/saved-search-results/${id}`;
    const res = await axios.get(url);
    if (res && res.data) return normalizeCollection(res.data);
  } catch (err) {
    console.warn(`Backend saved-search-results/${id} fetch failed:`, err);
  }

  const local = getLocalCollections();
  const found = local.find((c) => String(c.id) === String(id));
  return found ? normalizeCollection(found) : null;
};

export const createSavedSearchResult = async ({
  name,
  entityType,
  filters = {},
  limit = 20,
  items = [],
}) => {
  const parsedLimit = Number(limit) || 20;
  const slicedItems = (items || []).slice(0, parsedLimit);
  const normalizedEntityType = entityType || "researchers";

  // Payload strictly conforms to backend schema (saved-search-results-curl-guide.md)
  const payload = {
    name: (name || "").trim(),
    entity_type: normalizedEntityType,
    limit: parsedLimit,
    filters: filters || {},
    items: slicedItems,
  };

  try {
    const url = "/user/saved-search-results";
    const res = await axios.post(url, payload);
    if (res && res.data) {
      const saved = normalizeCollection(res.data);
      // Cache locally as mirror
      const local = getLocalCollections();
      saveLocalCollections([saved, ...local.filter((c) => c.id !== saved.id)]);
      return saved;
    }
  } catch (err) {
    console.error("Failed to post saved-search-results to backend:", err);
    // If server responded with an error (e.g. 422 limit reached or validation error), throw it
    if (err?.response?.status && err.response.status !== 404) {
      throw err;
    }
  }

  // Fallback to local storage only when endpoint not available (404) or network down
  const fallbackRecord = normalizeCollection({
    id: `saved_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    ...payload,
    createdAt: new Date().toISOString(),
  });
  const local = getLocalCollections();
  const updated = [fallbackRecord, ...local];
  saveLocalCollections(updated);
  return fallbackRecord;
};

export const updateSavedSearchResult = async (id, updates) => {
  try {
    const url = `/user/saved-search-results/${id}`;
    // Backend expects { "name": "..." }
    const putBody = updates.name ? { name: updates.name.trim() } : updates;
    const res = await axios.put(url, putBody);
    if (res && res.data) {
      const updated = normalizeCollection(res.data);
      const local = getLocalCollections();
      const index = local.findIndex((c) => String(c.id) === String(id));
      if (index !== -1) {
        local[index] = { ...local[index], ...updated };
        saveLocalCollections(local);
      }
      return updated;
    }
  } catch (err) {
    console.error("Failed to update saved-search-results on backend:", err);
    if (err?.response?.status && err.response.status !== 404) {
      throw err;
    }
  }

  const local = getLocalCollections();
  const index = local.findIndex((c) => String(c.id) === String(id));
  if (index !== -1) {
    local[index] = normalizeCollection({
      ...local[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    saveLocalCollections(local);
    return local[index];
  }
  return null;
};

export const deleteSavedSearchResult = async (id) => {
  try {
    const url = `/user/saved-search-results/${id}`;
    await axios.delete(url);
  } catch (err) {
    console.error("Failed to delete saved-search-results on backend:", err);
    if (err?.response?.status && err.response.status !== 404) {
      throw err;
    }
  }

  const local = getLocalCollections();
  const filtered = local.filter((c) => String(c.id) !== String(id));
  saveLocalCollections(filtered);
  return true;
};
